/**
 * The renewals pager — ONE implementation, now shared by the register (U5.5) and the
 * customer reminder draft queue (U2 / 5.6).
 *
 * ⚠ IT IS EXTRACTED RATHER THAN COPIED, AND THE REASON IS THE PROPERTY IT CARRIES.
 * The arithmetic below IS the honest-count rule — "a truncated list SAYS it is truncated" —
 * and U5.5's own defect is what that rule was written from: a page-length count told an
 * operator "0 contracts" over a full page of them. A second copy of this on the drafts
 * screen would be a second place for that defect to come back, and the test that pins it
 * would only be watching one of them.
 *
 * Nothing about the markup changed in the move; `renewals.test.tsx`'s existing cases
 * (`truncated-note` present when truncated, ABSENT when the page holds everything) pass
 * unedited, which is what says the extraction was a move rather than a rewrite.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';

export function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (next: number) => void;
}): React.JSX.Element {
  /*
   * ⚠ THE HONEST COUNT. `total` is the SERVER's count over the whole filtered set; `shown`
   * is how many rows are actually on screen. Deriving the range from `perPage` alone would
   * over-claim on the last page, and deriving `total` from `shown` would under-report by
   * however many pages nobody has clicked.
   */
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;
  const truncated = lastOnPage < total;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">
        {truncated ? (
          // The list SAYS it is truncated. Silence here reads as "that is all of them".
          <span data-testid="truncated-note">
            Showing {shown} of {total} — this list is truncated.
          </span>
        ) : (
          <span>{perPage} per page</span>
        )}
      </p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex size-8 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
